/**
 * UI date formats — DD.MM.YY HH:mm:ss in CPanel Broker Server Time Zone.
 * Storage/API: UTC. Display: fxb_broker_timezone (IANA, e.g. Asia/Dubai).
 */
import moment from "moment";
import { useEffect, useState } from "react";

/** Display datetime (Position Report / History / Order Book). */
export const UI_DATETIME_FORMAT = "DD.MM.YY HH:mm:ss";

/** Display date-only. */
export const UI_DATE_FORMAT = "DD.MM.YY";

/** react-datepicker `dateFormat` for filters (date-only). */
export const UI_DATEPICKER_FORMAT = "dd.MM.yy";

export const BROKER_TZ_KEY = "fxb_broker_timezone";

/** Windows TimeZoneInfo.Id → IANA (browsers / Intl only accept IANA). */
const WINDOWS_TO_IANA = {
  "India Standard Time": "Asia/Kolkata",
  "Arabian Standard Time": "Asia/Dubai",
  "Gulf Standard Time": "Asia/Dubai",
  "Pakistan Standard Time": "Asia/Karachi",
  "Sri Lanka Standard Time": "Asia/Colombo",
  "Bangladesh Standard Time": "Asia/Dhaka",
  "Singapore Standard Time": "Asia/Singapore",
  "China Standard Time": "Asia/Shanghai",
  "Tokyo Standard Time": "Asia/Tokyo",
  "GMT Standard Time": "Europe/London",
  "UTC": "UTC",
  "Greenwich Standard Time": "UTC",
};

const ALIAS_TO_IANA = {
  dubai: "Asia/Dubai",
  uae: "Asia/Dubai",
  gst: "Asia/Dubai",
  ist: "Asia/Kolkata",
  india: "Asia/Kolkata",
};

const OFFSET_TO_IANA = {
  "+00:00": "UTC",
  "-00:00": "UTC",
  "+01:00": "Europe/Berlin",
  "+02:00": "Europe/Athens",
  "+03:00": "Asia/Riyadh",
  "+03:30": "Asia/Tehran",
  "+04:00": "Asia/Dubai",
  "+04:30": "Asia/Kabul",
  "+05:00": "Asia/Karachi",
  "+05:30": "Asia/Kolkata",
  "+05:45": "Asia/Kathmandu",
  "+06:00": "Asia/Dhaka",
  "+07:00": "Asia/Bangkok",
  "+08:00": "Asia/Singapore",
  "+09:00": "Asia/Tokyo",
  "+09:30": "Australia/Adelaide",
  "+10:00": "Australia/Sydney",
};

function normalizeUtcOffset(raw) {
  if (raw == null || raw === "") return "";
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const sec = Math.abs(raw) <= 18 ? raw * 3600 : raw;
    const sign = sec < 0 ? "-" : "+";
    const abs = Math.abs(sec);
    const hh = String(Math.floor(abs / 3600)).padStart(2, "0");
    const mm = String(Math.floor((abs % 3600) / 60)).padStart(2, "0");
    return `${sign}${hh}:${mm}`;
  }
  const m = String(raw).trim().match(/([+-])(\d{1,2}):?(\d{2})/);
  if (!m) return "";
  return `${m[1]}${m[2].padStart(2, "0")}:${m[3]}`;
}

function ianaFromUtcOffset(offsetText, rawSeconds) {
  const off = normalizeUtcOffset(offsetText) || normalizeUtcOffset(rawSeconds);
  return (off && OFFSET_TO_IANA[off]) || "";
}

function unwrapTimeInfoBody(payload) {
  let body = payload;
  if (typeof body === "string") {
    try {
      body = JSON.parse(body);
    } catch {
      return null;
    }
  }
  if (!body || typeof body !== "object") return null;
  if (typeof body.data === "string") {
    try {
      body = { ...body, data: JSON.parse(body.data) };
    } catch {
      /* keep */
    }
  }
  const nested = body.data?.data && typeof body.data.data === "object" ? body.data.data : null;
  const inner = body.data && typeof body.data === "object" && !Array.isArray(body.data) ? body.data : null;
  if (bodyHasTimeInfo(body)) return body;
  if (bodyHasTimeInfo(inner)) return inner;
  if (bodyHasTimeInfo(nested)) return nested;
  return findTimeInfoBodyDeep(body, 0) || inner || body;
}

function bodyHasTimeInfo(body) {
  if (!body || typeof body !== "object") return false;
  return Boolean(
    body.timezone ||
      body.Timezone ||
      body.timeZone ||
      body.TimeZone ||
      body.datetime ||
      body.Datetime ||
      body.utc_offset ||
      body.Utc_offset ||
      body.utcOffset ||
      body.unixtime ||
      body.Unixtime
  );
}

function findTimeInfoBodyDeep(obj, depth) {
  if (!obj || typeof obj !== "object" || depth > 5) return null;
  if (bodyHasTimeInfo(obj)) return obj;
  const values = Array.isArray(obj) ? obj : Object.values(obj);
  for (const v of values) {
    const found = findTimeInfoBodyDeep(v, depth + 1);
    if (found) return found;
  }
  return null;
}

function pickTimeInfoTimezone(body) {
  if (!body || typeof body !== "object") return "";
  const direct = String(
    body.timezone || body.Timezone || body.timeZone || body.TimeZone || ""
  ).trim();
  const mapped = normalizeBrokerTimeZoneId(direct);
  if (isUsableIanaTimeZone(mapped)) return mapped;
  const datetime = String(body.datetime || body.Datetime || "");
  const dtOff = datetime.match(/([+-]\d{2}:?\d{2}|Z)\s*$/i)?.[1] || "";
  const fromOff = ianaFromUtcOffset(
    body.utc_offset || body.Utc_offset || body.utcOffset || dtOff,
    body.raw_offset ?? body.Raw_offset ?? body.rawOffset
  );
  if (isUsableIanaTimeZone(fromOff)) return fromOff;
  return "";
}

function notifyBrokerTimeZoneChanged() {
  try {
    window.dispatchEvent(new Event("fxb-broker-timezone"));
  } catch {
    /* ignore */
  }
}

let lastTimeInfoMeta = { unixtime: 0, abbreviation: "", datetime: "" };

export function getTimeInfoMeta() {
  return lastTimeInfoMeta;
}

function normalizeBrokerTimeZoneId(raw) {
  const tz = String(raw || "").trim();
  if (!tz || tz.toLowerCase() === "null") return "";
  if (WINDOWS_TO_IANA[tz]) return WINDOWS_TO_IANA[tz];
  const alias = ALIAS_TO_IANA[tz.toLowerCase()];
  if (alias) return alias;
  return tz;
}

/** Persist broker IANA zone from /common/TimeInfo (call on app boot). */
export function isUsableIanaTimeZone(tz) {
  const n = normalizeBrokerTimeZoneId(tz);
  return Boolean(n && n !== "UTC" && isValidIanaTimeZone(n));
}

export function setBrokerTimeZoneId(raw) {
  const tz = normalizeBrokerTimeZoneId(raw);
  if (!isUsableIanaTimeZone(tz)) return "";
  try {
    sessionStorage.setItem(BROKER_TZ_KEY, tz);
    localStorage.setItem(BROKER_TZ_KEY, tz);
  } catch {
    /* ignore */
  }
  notifyBrokerTimeZoneChanged();
  return tz;
}

/** Read timezone from TimeInfo JSON (flat, wrapped, or encrypted-then-decrypted). */
export function applyBrokerTimeZoneFromTimeInfo(payload) {
  const body = unwrapTimeInfoBody(payload);
  if (body && typeof body === "object") {
    lastTimeInfoMeta = {
      unixtime: Number(body.unixtime ?? body.Unixtime ?? 0) || 0,
      abbreviation: String(body.abbreviation || body.Abbreviation || ""),
      datetime: String(body.datetime || body.Datetime || ""),
    };
  }
  const tz = pickTimeInfoTimezone(body);
  if (!tz) return "";
  return setBrokerTimeZoneId(tz);
}

function readStoredBrokerTz(raw) {
  const tz = normalizeBrokerTimeZoneId(raw);
  return isUsableIanaTimeZone(tz) ? tz : "";
}

function getBrokerTimeZone() {
  try {
    const fromSession = readStoredBrokerTz(sessionStorage.getItem(BROKER_TZ_KEY));
    if (fromSession) return fromSession;
    const fromLocal = readStoredBrokerTz(localStorage.getItem(BROKER_TZ_KEY));
    if (fromLocal) return fromLocal;
  } catch {
    /* ignore */
  }
  return "";
}

/** Active broker IANA zone. */
export function getBrokerTimeZoneId() {
  return getBrokerTimeZone();
}

/** Subscribe to TimeInfo timezone so memoized date cells refresh after boot. */
export function useBrokerTimeZoneId() {
  const [tz, setTz] = useState(() => getBrokerTimeZone());
  useEffect(() => {
    const sync = () => setTz(getBrokerTimeZone());
    sync();
    window.addEventListener("fxb-broker-timezone", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("fxb-broker-timezone", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return tz;
}

function isValidIanaTimeZone(tz) {
  if (!tz || typeof tz !== "string") return false;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: tz }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

function resolveUsableBrokerTz() {
  const tz = getBrokerTimeZone();
  if (isUsableIanaTimeZone(tz)) return tz;
  return "Asia/Kolkata";
}

/** YYYY-MM-DD for API filters — calendar day in broker server time. */
export function formatApiDateYmd(d) {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: resolveUsableBrokerTz(),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d instanceof Date ? d : d ? new Date(d) : new Date());
    const get = (type) => parts.find((p) => p.type === type)?.value || "";
    const y = get("year");
    const m = get("month");
    const day = get("day");
    if (y && m && day) return `${y}-${m}-${day}`;
  } catch {
    /* fall through */
  }
  const fallback = moment(d);
  return fallback.isValid() ? fallback.format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
}

/** Full moment ISO string (position report day/valan windows). */
export function formatApiDateMomentIso(d) {
  const m = moment(d);
  return m.isValid() ? m.format() : moment().format();
}

const PARSE_FORMATS = [
  "DD.MM.YY HH:mm:ss",
  "DD.MM.YYYY HH:mm:ss",
  "DD-MM-YYYY HH:mm:ss",
  "DD-MM-YYYY hh:mm:ss A",
  "DD-MM-YYYY hh:mm A",
  "DD-MM-YYYY HH:mm",
  "DD-MM-YYYY",
  "DD.MM.YY",
  "DD.MM.YYYY",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm:ssZ",
  "YYYY-MM-DD",
  moment.ISO_8601,
];

export function parseUiMoment(val) {
  if (val == null || val === "") return null;
  if (moment.isMoment(val)) return val.isValid() ? val : null;
  if (val instanceof Date) {
    const m = moment(val);
    return m.isValid() ? m : null;
  }
  const m = moment(val, PARSE_FORMATS, true);
  if (m.isValid()) return m;
  const loose = moment(val);
  return loose.isValid() ? loose : null;
}

function parseNaiveUtcDate(y, mo, d, hh, mi, ss, ms = 0) {
  return new Date(Date.UTC(y, mo, d, hh, mi, ss, ms));
}

function parse12Hour(hh, ampmToken) {
  if (!ampmToken) return hh;
  const pm = String(ampmToken).toUpperCase() === "PM";
  if (pm && hh < 12) return hh + 12;
  if (!pm && hh === 12) return 0;
  return hh;
}

/**
 * Parse API/DB instants as absolute UTC.
 * Handles: DateTimeOffset JSON, SQL FORMAT dd-MM-yyyy, fractional seconds, naive UTC wall strings.
 */
export function parseApiValueAsUtcDate(val) {
  if (val == null || val === "") return null;
  if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : val;
  if (moment.isMoment(val)) return val.isValid() ? val.toDate() : null;

  const original = String(val).trim();
  if (!original) return null;

  // SQL FORMAT / AddTime: "13-08-2026 12:30:45" — match BEFORE replacing space with T.
  const dmy = original.match(
    /^(\d{2})[.\-/](\d{2})[.\-/](\d{2,4})(?:[T\s]+(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?)?(?:\s*(AM|PM|am|pm))?(?:\s*([Zz]|[+-]\d{2}:?\d{2}))?$/
  );
  if (dmy) {
    let year = Number(dmy[3]);
    if (year < 100) year += 2000;
    const hh = parse12Hour(Number(dmy[4] || 0), dmy[7]);
    return parseNaiveUtcDate(
      year,
      Number(dmy[2]) - 1,
      Number(dmy[1]),
      hh,
      Number(dmy[5] || 0),
      Number(dmy[6] || 0)
    );
  }

  let text = original.replace(" ", "T").replace(/(\.\d+)\s+([+-]\d{2}:?\d{2})$/, "$1$2");
  text = text.replace(/(\.\d{3})\d+/, "$1");

  const hasZone =
    /[Zz]$/.test(text) ||
    /[+-]\d{2}:\d{2}$/.test(text) ||
    /[+-]\d{4}$/.test(text);

  if (hasZone) {
    const d = new Date(text);
    if (!Number.isNaN(d.getTime())) return d;
  }

  const iso = text.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?$/
  );
  if (iso) {
    const ms = iso[7] ? Number(String(iso[7]).padEnd(3, "0").slice(0, 3)) : 0;
    return parseNaiveUtcDate(
      Number(iso[1]),
      Number(iso[2]) - 1,
      Number(iso[3]),
      Number(iso[4] || 0),
      Number(iso[5] || 0),
      Number(iso[6] || 0),
      ms
    );
  }

  return null;
}

function partsInBrokerZone(date) {
  const tz = resolveUsableBrokerTz();
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      hour12: false,
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value || "";
    let hh = get("hour");
    // Some engines emit "24" for midnight
    if (hh === "24") hh = "00";
    return {
      dd: get("day"),
      mm: get("month"),
      yy: get("year"),
      hh,
      mi: get("minute"),
      ss: get("second"),
    };
  } catch {
    const p = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      hour12: false,
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(date);
    const get = (type) => p.find((x) => x.type === type)?.value || "";
    return {
      dd: get("day"),
      mm: get("month"),
      yy: get("year"),
      hh: get("hour") === "24" ? "00" : get("hour"),
      mi: get("minute"),
      ss: get("second"),
    };
  }
}

/** Position-report style datetime for any UI cell. */
export function formatUiDateTime(val, empty = "") {
  const d = parseApiValueAsUtcDate(val);
  if (!d) return empty;
  const p = partsInBrokerZone(d);
  return `${p.dd}.${p.mm}.${p.yy} ${p.hh}:${p.mi}:${p.ss}`;
}

/** Date-only display DD.MM.YY */
export function formatUiDate(val, empty = "") {
  const d = parseApiValueAsUtcDate(val);
  if (!d) return empty;
  const p = partsInBrokerZone(d);
  return `${p.dd}.${p.mm}.${p.yy}`;
}

/** Split broker datetime into date + time for compact statement rows. */
export function formatUiDateTimeParts(val) {
  const full = formatUiDateTime(val, "");
  if (!full) return { date: "—", time: "", full: "—" };
  const space = full.indexOf(" ");
  if (space < 0) return { date: full, time: "", full };
  return {
    date: full.slice(0, space),
    time: full.slice(space + 1),
    full,
  };
}

/** Time-only (HH:mm:ss) in broker server zone — dashboard TIME columns. */
export function formatUiTime(val, empty = "") {
  const { time } = formatUiDateTimeParts(val);
  return time || empty;
}

/** react-table Cell: persist UTC, show CPanel broker server time. */
export function brokerDateTimeCell({ value, row }) {
  const src =
    row?.original?.createdOn ??
    row?.original?.CreatedOn ??
    value;
  return formatUiDateTime(src, "-");
}

/** @deprecated Prefer formatUiDate — always broker-server time. */
export function formatDate(val) {
  if (!val) return null;
  return formatUiDate(val, null);
}

function tzOffsetMsAt(utcMs, tz) {
  const d = new Date(utcMs);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(d);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  let hh = get("hour");
  if (hh === 24) hh = 0;
  const wallAsUtc = Date.UTC(get("year"), get("month") - 1, get("day"), hh, get("minute"), get("second"));
  return wallAsUtc - utcMs;
}

export function brokerWallClockToUtcDate(year, monthIndex, day, hour, minute, second) {
  const tz = resolveUsableBrokerTz();
  let utc = Date.UTC(year, monthIndex, day, hour, minute, second);
  const off1 = tzOffsetMsAt(utc, tz);
  utc -= off1;
  const off2 = tzOffsetMsAt(utc, tz);
  if (off2 !== off1) utc = Date.UTC(year, monthIndex, day, hour, minute, second) - off2;
  return new Date(utc);
}

export function formatBrokerDatetimeLocal(val, empty = "") {
  const d = parseApiValueAsUtcDate(val);
  if (!d) return empty;
  const tz = resolveUsableBrokerTz();
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(d);
    const get = (type) => parts.find((p) => p.type === type)?.value || "";
    let hh = get("hour");
    if (hh === "24") hh = "00";
    const y = get("year");
    const mo = get("month");
    const day = get("day");
    if (!y || !mo || !day) return empty;
    return `${y}-${mo}-${day}T${hh}:${get("minute")}:${get("second")}`;
  } catch {
    return empty;
  }
}

export function parseBrokerDatetimeLocalToUtcIso(v) {
  const text = String(v || "").trim();
  if (!text) return null;
  const m = text.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  const d = brokerWallClockToUtcDate(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
    Number(m[6] || 0)
  );
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
}

const EXPIRY_MMM = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

export function formatBrokerExpiryName(val) {
  const text = String(val || "").trim();
  if (!text) return "";
  const hasZone = /[Zz]$/.test(text) || /[+-]\d{2}:\d{2}/.test(text);
  if (!hasZone) {
    const m = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const month = Number(m[2]);
      if (month >= 1 && month <= 12) return `${m[3]}${EXPIRY_MMM[month - 1]}${m[1].slice(-2)}`;
    }
  }
  const d = parseApiValueAsUtcDate(val);
  if (!d) return "";
  const p = partsInBrokerZone(d);
  const month = Number(p.mm);
  if (!month) return "";
  return `${p.dd}${EXPIRY_MMM[month - 1]}${p.yy}`;
}
